import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Group,
  NativeSelect,
  Stack,
  Table,
  Text,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconDownload, IconUpload } from '@/components/icons'
import { AppModal } from '@/components/ui'
import { api, ApiError } from '@/lib/api'
import { downloadBlob } from '@/lib/download-blob'
import {
  KNOWLEDGE_GENERAL_TAB_ID,
  countByType,
  isKnowledgeGeneralTab,
} from '@/components/knowledge/knowledge-base-shared'
import type {
  ProjectRow,
  SupportKnowledgeArticleRow,
  SupportKnowledgeMaterialType,
} from '@/types/api'

type MaterialTypeOption = { key: string; label: string }

type ScopeValue = 'all' | typeof KNOWLEDGE_GENERAL_TAB_ID | string

type Props = {
  opened: boolean
  onClose: () => void
  projects: ProjectRow[]
  articles: SupportKnowledgeArticleRow[]
  materialTypes: MaterialTypeOption[]
  selectedProjectId: string | null
  canImport: boolean
  onImported: () => void | Promise<void>
}

function scopeLabel(scope: ScopeValue, projects: ProjectRow[]): string {
  if (scope === 'all') return 'Все разделы'
  if (isKnowledgeGeneralTab(scope)) return 'Общие'
  const project = projects.find((p) => p.id === scope)
  return project?.name || project?.slug || 'Проект'
}

function exportFilename(scope: ScopeValue, materialType?: string): string {
  const parts = ['knowledge_base']
  if (isKnowledgeGeneralTab(scope)) parts.push('general')
  else if (scope !== 'all') parts.push(scope.slice(0, 8))
  if (materialType) parts.push(materialType)
  return `${parts.join('_')}.xlsx`
}

function exportParams(scope: ScopeValue, materialType?: string) {
  const params: {
    project_id?: string
    general_only?: boolean
    material_type?: string
    include_inactive?: boolean
  } = { include_inactive: true }
  if (isKnowledgeGeneralTab(scope)) {
    params.general_only = true
  } else if (scope !== 'all') {
    params.project_id = scope
  }
  if (materialType) params.material_type = materialType
  return params
}

function uploadParams(scope: ScopeValue, materialType?: string) {
  const params: {
    project_id?: string
    general_only?: boolean
    material_type?: string
  } = {}
  if (isKnowledgeGeneralTab(scope)) {
    params.general_only = true
  } else if (scope !== 'all') {
    params.project_id = scope
  }
  if (materialType) params.material_type = materialType
  return params
}

function filterArticlesByScope(
  articles: SupportKnowledgeArticleRow[],
  scope: ScopeValue,
): SupportKnowledgeArticleRow[] {
  if (scope === 'all') return articles
  if (isKnowledgeGeneralTab(scope)) return articles.filter((a) => !a.project_id)
  return articles.filter((a) => a.project_id === scope)
}

export function KnowledgeBaseExcelPanel({
  opened,
  onClose,
  projects,
  articles,
  materialTypes,
  selectedProjectId,
  canImport,
  onImported,
}: Props) {
  const initialScope: ScopeValue =
    selectedProjectId && !isKnowledgeGeneralTab(selectedProjectId) && selectedProjectId !== 'all'
      ? selectedProjectId
      : isKnowledgeGeneralTab(selectedProjectId)
        ? KNOWLEDGE_GENERAL_TAB_ID
        : 'all'

  const [scope, setScope] = useState<ScopeValue>(initialScope)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({})

  useEffect(() => {
    if (!opened) return
    const nextScope: ScopeValue =
      selectedProjectId && !isKnowledgeGeneralTab(selectedProjectId)
        ? selectedProjectId
        : isKnowledgeGeneralTab(selectedProjectId)
          ? KNOWLEDGE_GENERAL_TAB_ID
          : 'all'
    setScope(nextScope)
  }, [opened, selectedProjectId])

  const scopedArticles = useMemo(
    () => filterArticlesByScope(articles, scope),
    [articles, scope],
  )
  const counts = useMemo(() => countByType(scopedArticles), [scopedArticles])

  const scopeOptions = useMemo(
    () => [
      { value: 'all', label: 'Все разделы' },
      { value: KNOWLEDGE_GENERAL_TAB_ID, label: 'Общие' },
      ...projects.map((p) => ({
        value: p.id,
        label: p.name || p.slug || p.id,
      })),
    ],
    [projects],
  )

  const runExport = async (materialType?: SupportKnowledgeMaterialType) => {
    const key = materialType ? `export-${materialType}` : 'export-all'
    setBusyKey(key)
    try {
      const blob = await api.supportKnowledgeExportExcel(exportParams(scope, materialType))
      downloadBlob(blob, exportFilename(scope, materialType))
    } catch (e) {
      showNotification({
        title: 'Ошибка выгрузки',
        message: e instanceof ApiError ? e.message : 'Не удалось выгрузить Excel',
        color: 'red',
      })
    } finally {
      setBusyKey(null)
    }
  }

  const runImport = async (file: File, materialType?: SupportKnowledgeMaterialType) => {
    const key = materialType ? `import-${materialType}` : 'import-all'
    setBusyKey(key)
    try {
      const res = await api.supportKnowledgeUploadExcel(file, uploadParams(scope, materialType))
      showNotification({
        title: 'Импорт завершён',
        message: res.message || `Создано: ${res.created ?? 0}, обновлено: ${res.updated ?? 0}`,
        color: 'green',
      })
      await onImported()
    } catch (e) {
      showNotification({
        title: 'Ошибка импорта',
        message: e instanceof ApiError ? e.message : 'Не удалось загрузить Excel',
        color: 'red',
      })
    } finally {
      setBusyKey(null)
    }
  }

  return (
    <AppModal opened={opened} onClose={onClose} title="Импорт и экспорт Excel" size="lg">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Выгрузка и загрузка материалов по типам. Полная выгрузка создаёт файл с отдельным листом
          для каждого типа. При импорте лист определяет тип материала; колонка «раздел» — Общие или
          название проекта. Если указан id — запись обновляется.
        </Text>

        <NativeSelect
          label="Раздел"
          description="Ограничить выгрузку/импорт одним разделом или взять все материалы workspace"
          value={scope}
          onChange={(e) => setScope(e.currentTarget.value as ScopeValue)}
          data={scopeOptions}
        />

        <Group justify="space-between">
          <Text fw={600} size="sm">
            {scopeLabel(scope, projects)}
          </Text>
          <Button
            size="compact-sm"
            variant="light"
            leftSection={<IconDownload size={14} stroke={1.5} />}
            loading={busyKey === 'export-all'}
            onClick={() => void runExport()}
          >
            Выгрузить все типы
          </Button>
        </Group>

        <Table striped withTableBorder highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Тип материала</Table.Th>
              <Table.Th w={80}>Записей</Table.Th>
              <Table.Th w={220}>Действия</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {materialTypes.map((type) => {
              const count = counts.get(type.key) ?? 0
              const inputKey = type.key
              return (
                <Table.Tr key={type.key}>
                  <Table.Td>{type.label}</Table.Td>
                  <Table.Td>{count}</Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Button
                        size="compact-xs"
                        variant="default"
                        leftSection={<IconDownload size={14} stroke={1.5} />}
                        loading={busyKey === `export-${type.key}`}
                        onClick={() => void runExport(type.key as SupportKnowledgeMaterialType)}
                      >
                        Excel
                      </Button>
                      {canImport ? (
                        <>
                          <input
                            ref={(el) => {
                              fileRefs.current[inputKey] = el
                            }}
                            type="file"
                            accept=".xlsx,.xls"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files?.[0]
                              if (!file) return
                              void runImport(file, type.key as SupportKnowledgeMaterialType).finally(() => {
                                e.target.value = ''
                              })
                            }}
                          />
                          <Button
                            size="compact-xs"
                            variant="light"
                            leftSection={<IconUpload size={14} stroke={1.5} />}
                            loading={busyKey === `import-${type.key}`}
                            onClick={() => fileRefs.current[inputKey]?.click()}
                          >
                            Загрузить
                          </Button>
                        </>
                      ) : null}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>

        {canImport ? (
          <>
            <input
              ref={(el) => {
                fileRefs.current.__all__ = el
              }}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (!file) return
                void runImport(file).finally(() => {
                  e.target.value = ''
                })
              }}
            />
            <Button
              variant="light"
              leftSection={<IconUpload size={16} stroke={1.5} />}
              loading={busyKey === 'import-all'}
              onClick={() => fileRefs.current.__all__?.click()}
            >
              Загрузить файл со всеми листами
            </Button>
          </>
        ) : null}
      </Stack>
    </AppModal>
  )
}
